const HUBSPOT_SCRIPT_SRC = "//js.hsforms.net/forms/embed/v2.js";
const MUNCHKIN_SCRIPT_SRC = "//munchkin.marketo.net/munchkin.js";
const HUBSPOT_FORM_HOST_ID = "roi-hubspot-form-host";
const HIDDEN_INTEGRATION_HOST_STYLE =
  "position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;";
const HUBSPOT_FORM_READY_TIMEOUT_MS = 20000;

let munchkinInitialized = false;
let munchkinInitPromise = null;
let hubspotFormRef = null;
let hubspotFormReadyResolve = null;
let hubspotInitPromise = null;
let hubspotPostMessageBound = false;

let hubspotFormReady = createHubspotFormReadyPromise();

function createHubspotFormReadyPromise() {
  return new Promise((resolve) => {
    hubspotFormReadyResolve = resolve;
  });
}

function resetHubspotFormState() {
  hubspotFormRef = null;
  hubspotInitPromise = null;
  hubspotFormReady = createHubspotFormReadyPromise();
}

export function isHubspotIntegrationEnabled() {
  const raw = String(process.env.REACT_APP_HUBSPOT_ENABLE || "").trim().toLowerCase();
  if (raw === "false" || raw === "0" || raw === "no") return false;
  if (raw === "true" || raw === "1" || raw === "yes") return true;
  return !!(
    process.env.REACT_APP_HUBSPOT_PORTAL_ID && process.env.REACT_APP_HUBSPOT_FORM_ID
  );
}

function isHubspotFormConnected() {
  if (!hubspotFormRef) return false;
  const formEl = getHubSpotFormElement(hubspotFormRef);
  return !!(formEl && document.body.contains(formEl));
}

function resolveScriptSrc(src) {
  if (typeof window === "undefined" || !src?.startsWith("//")) return src;
  return `${window.location.protocol}${src}`;
}

function loadScript(src) {
  const resolvedSrc = resolveScriptSrc(src);

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${resolvedSrc}"], script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = resolvedSrc;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function ensureHubspotFormHost() {
  if (typeof document === "undefined") return null;

  let host = document.getElementById(HUBSPOT_FORM_HOST_ID);
  if (host) return host;

  host = document.createElement("div");
  host.id = HUBSPOT_FORM_HOST_ID;
  host.className = HUBSPOT_FORM_HOST_ID;
  host.setAttribute("aria-hidden", "true");
  host.style.cssText = HIDDEN_INTEGRATION_HOST_STYLE;
  document.body.appendChild(host);
  return host;
}

export function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export function ensureMunchkinReady(munchkinId = "362-YWJ-377") {
  if (!munchkinId) return Promise.resolve();

  const tryInit = () => {
    if (window.Munchkin && typeof window.Munchkin.init === "function") {
      if (!munchkinInitialized) {
        window.Munchkin.init(munchkinId);
        munchkinInitialized = true;
        console.log("[ROI Munchkin] initialized:", munchkinId);
      }
      return true;
    }
    return false;
  };

  if (tryInit()) return Promise.resolve();
  if (munchkinInitPromise) return munchkinInitPromise;

  const pollForMunchkin = (resolve, reject) => {
    let attempts = 24;
    const tick = () => {
      if (tryInit()) {
        resolve();
        return;
      }
      if (attempts <= 0) {
        reject(new Error("[ROI Munchkin] Munchkin global not available after script load"));
        return;
      }
      attempts -= 1;
      setTimeout(tick, 250);
    };
    tick();
  };

  munchkinInitPromise = new Promise((resolve, reject) => {
    const resolvedSrc = resolveScriptSrc(MUNCHKIN_SCRIPT_SRC);
    const existing = document.querySelector(
      `script[src="${resolvedSrc}"], script[src="${MUNCHKIN_SCRIPT_SRC}"]`
    );

    if (existing) {
      pollForMunchkin(resolve, (err) => {
        munchkinInitPromise = null;
        reject(err);
      });
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = resolvedSrc;

    const onScriptLoaded = () => {
      pollForMunchkin(resolve, (err) => {
        munchkinInitPromise = null;
        reject(err);
      });
    };

    script.onload = onScriptLoaded;
    script.onreadystatechange = function onMunchkinReadyState() {
      if (this.readyState === "complete" || this.readyState === "loaded") {
        onScriptLoaded();
      }
    };
    script.onerror = () => {
      munchkinInitPromise = null;
      reject(new Error(`[ROI Munchkin] Failed to load script: ${resolvedSrc}`));
    };

    document.head.appendChild(script);
  }).catch((err) => {
    munchkinInitPromise = null;
    throw err;
  });

  return munchkinInitPromise;
}

export function initMunchkin(munchkinId = "362-YWJ-377") {
  return ensureMunchkinReady(munchkinId);
}

export async function trackMunchkinPageVisit(url) {
  const munchkinId = process.env.REACT_APP_MUNCHKIN_ID || "362-YWJ-377";

  try {
    await ensureMunchkinReady(munchkinId);
  } catch (err) {
    console.warn("[ROI Munchkin] init failed:", err);
    return false;
  }

  const visitUrl =
    url ||
    (typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search || ""}`
      : "");

  if (window.Munchkin && typeof window.Munchkin.munchkinFunction === "function") {
    window.Munchkin.munchkinFunction("visitWebPage", { url: visitUrl });
    console.log("[ROI Munchkin] visitWebPage sent:", visitUrl);
    return true;
  }

  console.warn("[ROI Munchkin] munchkinFunction not available");
  return false;
}

function loadHubspotScript() {
  if (window.hbspt?.forms?.create) return Promise.resolve();
  return loadScript(HUBSPOT_SCRIPT_SRC);
}

function getHubSpotFormElement($form) {
  if (!$form) return null;
  if (typeof $form.get === "function") return $form.get(0);
  if ($form instanceof HTMLElement) return $form;
  return null;
}

function pickHubSpotFieldNodes($form, selector) {
  if (typeof $form?.find === "function") {
    const $fields = $form.find(selector);
    return $fields.length ? $fields.toArray() : null;
  }

  const formEl = getHubSpotFormElement($form);
  if (!formEl) return null;
  const matches = Array.from(formEl.querySelectorAll(selector));
  return matches.length ? matches : null;
}

function bindHubspotPostMessageListener() {
  if (hubspotPostMessageBound || typeof window === "undefined") return;
  hubspotPostMessageBound = true;

  window.addEventListener("message", (event) => {
    const data = event?.data;
    if (!data || typeof data !== "object") return;
    if (!data.formGuid && data.accepted === undefined) return;

    if (data.accepted === true) {
      console.log("[ROI HubSpot] Lead accepted by HubSpot", data);
      trackMunchkinPageVisit();
    } else if (data.accepted === false) {
      console.warn("[ROI HubSpot] HubSpot rejected submission", data);
    }
  });
}

export function waitForHubspotForm(timeoutMs = HUBSPOT_FORM_READY_TIMEOUT_MS) {
  if (hubspotFormRef && isHubspotFormConnected()) {
    return Promise.resolve(hubspotFormRef);
  }

  return Promise.race([
    hubspotFormReady,
    delay(timeoutMs).then(() => {
      throw new Error(
        `[ROI HubSpot] Form not ready within ${timeoutMs}ms — check js.hsforms.net is not blocked`
      );
    }),
  ]);
}

export async function bootHubspotIntegration() {
  if (!isHubspotIntegrationEnabled()) {
    console.log(
      "[ROI HubSpot] Integration disabled. REACT_APP_HUBSPOT_ENABLE=",
      process.env.REACT_APP_HUBSPOT_ENABLE
    );
    return false;
  }

  const host = ensureHubspotFormHost();
  if (!host) {
    console.warn("[ROI HubSpot] Unable to create hidden form host");
    return false;
  }

  const munchkinId = process.env.REACT_APP_MUNCHKIN_ID || "362-YWJ-377";
  const portalId = process.env.REACT_APP_HUBSPOT_PORTAL_ID || "47271964";
  const formId =
    process.env.REACT_APP_HUBSPOT_FORM_ID || "a832c87d-5ad6-4fa4-acb0-8138423a9af5";
  const region = process.env.REACT_APP_HUBSPOT_REGION || "na1";

  try {
    await ensureMunchkinReady(munchkinId);
    await initHubspotForm({
      portalId,
      formId,
      region,
      target: `#${HUBSPOT_FORM_HOST_ID}`,
    });
    console.log("[ROI HubSpot] Integration ready");
    return true;
  } catch (err) {
    console.warn("[ROI HubSpot] boot failed:", err);
    return false;
  }
}

function isVisibleField(el) {
  return el && el.offsetParent !== null && !el.disabled && el.type !== "hidden";
}

function setNativeInputValue(field, val) {
  if (!field) return;

  try {
    const proto =
      field.tagName === "SELECT"
        ? window.HTMLSelectElement.prototype
        : field.tagName === "TEXTAREA"
          ? window.HTMLTextAreaElement.prototype
          : window.HTMLInputElement.prototype;
    const desc = Object.getOwnPropertyDescriptor(proto, "value");
    if (desc && desc.set) desc.set.call(field, val);
    else field.value = val;
  } catch (e) {
    field.value = val;
  }

  field.dispatchEvent(new Event("input", { bubbles: true }));
  field.dispatchEvent(new Event("change", { bubbles: true }));
  field.dispatchEvent(new Event("blur", { bubbles: true }));
}

function setHubSpotField($form, selector, value) {
  const val = (value == null ? "" : String(value)).trim();
  if (!val || !$form) return false;

  if (typeof $form.find === "function") {
    const $fields = $form.find(selector);
    if (!$fields.length) return false;

    const $target = $fields.first();
    $target.val(val);
    $target.trigger("input");
    $target.trigger("change");
    $target.trigger("blur");
    return true;
  }

  const matches = pickHubSpotFieldNodes($form, selector);
  const field = matches?.[0];
  if (!field) return false;

  setNativeInputValue(field, val);
  return true;
}

function readHubSpotFieldValue($form, selector) {
  if (typeof $form?.find === "function") {
    const $fields = $form.find(selector);
    if (!$fields.length) return "";
    return ($fields.first().val() || "").toString().trim();
  }

  const matches = pickHubSpotFieldNodes($form, selector);
  const field = matches?.[0];
  return field ? String(field.value || "").trim() : "";
}

function findHubSpotCompanyField($form) {
  const formEl = getHubSpotFormElement($form);
  if (!formEl) return null;

  let matches = Array.from(
    formEl.querySelectorAll(
      'input[name="company"], select[name="company"], textarea[name="company"]'
    )
  );

  if (!matches.length && typeof $form.find === "function") {
    matches = $form.find('input[name="company"], input[name*="company"], input[id*="company"]').toArray();
  }

  if (!matches.length) {
    const exclude =
      /(size|website|url|domain|phone|revenue|industry|employee|email|count|type|role|address|city|state|zip|postal|country)/i;
    matches = Array.from(
      formEl.querySelectorAll(
        'input[name*="company" i], input[id*="company" i], input[autocomplete="organization"], input[placeholder*="company" i]'
      )
    ).filter((el) => !exclude.test(`${el.name || ""} ${el.id || ""}`));
  }

  if (!matches.length) return null;
  return matches.find(isVisibleField) || matches[0];
}

function setHubSpotCompanyField($form, value) {
  const val = (value == null ? "" : String(value)).trim();
  if (!val) return false;

  const field = findHubSpotCompanyField($form);
  if (!field) {
    console.warn("[ROI HubSpot] Company field not found on HubSpot form");
    return false;
  }

  try {
    if (!field.getAttribute("autocomplete")) {
      field.setAttribute("autocomplete", "organization");
    }
  } catch (e) {
    // ignore
  }

  const jq = typeof window !== "undefined" ? window.jQuery || window.$ : null;
  if (jq) {
    jq(field).val(val).trigger("input").trigger("change").trigger("blur");
    return true;
  }

  setNativeInputValue(field, val);
  return true;
}

function stripLabelText(html) {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").trim().toLowerCase();
}

function getFieldLabelText(domNode, fieldMeta) {
  if (!domNode) return stripLabelText(fieldMeta?.text || fieldMeta?.label || "");

  const group = domNode.closest(".roi-form-group");
  const labelEl = group?.querySelector(".field-label");
  if (labelEl) return stripLabelText(labelEl.innerHTML);
  return stripLabelText(fieldMeta?.text || fieldMeta?.label || "");
}

function labelLooksLikeEmail(labelText) {
  const normalized = String(labelText || "")
    .toLowerCase()
    .replace(/-/g, "")
    .replace(/\s/g, "");
  return normalized.includes("email") || normalized.includes("mailid");
}

function normalizeLabelForMatch(labelText) {
  return String(labelText || "")
    .toLowerCase()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function labelLooksLikeNoOfAgents(labelText) {
  const normalized = normalizeLabelForMatch(labelText);
  if (!normalized.includes("agent")) return false;

  return (
    normalized.includes("no of agent") ||
    normalized.includes("number of agent") ||
    normalized.includes("num of agent") ||
    normalized.includes("no of agents") ||
    normalized.includes("number of agents") ||
    normalized === "agents" ||
    normalized === "no of agents" ||
    normalized === "number of agents"
  );
}

function labelLooksLikeLastName(labelText) {
  const normalized = normalizeLabelForMatch(labelText);
  return (
    normalized.includes("last name") ||
    normalized.includes("lastname") ||
    normalized.includes("surname") ||
    normalized.includes("family name") ||
    normalized === "last"
  );
}

function labelLooksLikeFirstName(labelText) {
  const normalized = normalizeLabelForMatch(labelText);
  if (labelLooksLikeLastName(normalized)) return false;

  return (
    normalized.includes("first name") ||
    normalized.includes("firstname") ||
    normalized.includes("given name") ||
    normalized === "first" ||
    normalized === "name" ||
    normalized.includes("full name")
  );
}

function resolveHubspotFieldAlias(fieldName) {
  const normalized = String(fieldName || "").trim().toLowerCase();
  if (normalized === "no_of_agents") return "number_of_agents";
  return fieldName;
}

export function patchRoiFieldMeta(target, fieldProps = {}) {
  if (!target) return target;

  const label = fieldProps.label || fieldProps.text || target.label || target.text || "";
  target.label = label;
  target.text = fieldProps.text || label;

  if (fieldProps.hubspot_field) {
    target.hubspot_field = resolveHubspotFieldAlias(fieldProps.hubspot_field);
    return target;
  }

  const inferred = inferHubspotFieldName(null, {
    ...target,
    type: target.type || fieldProps.type,
    label,
    text: target.text,
  });
  if (inferred) {
    target.hubspot_field = resolveHubspotFieldAlias(inferred);
  }

  return target;
}

export function inferHubspotFieldName(fieldUuid, fieldMeta, domNode) {
  if (fieldMeta?.hubspot_field) {
    return resolveHubspotFieldAlias(fieldMeta.hubspot_field);
  }

  const fieldType = String(fieldMeta?.type || "").toLowerCase();
  if (fieldType === "email") return "email";
  if (fieldType === "phonenumber") return "phone";

  if (domNode instanceof HTMLInputElement) {
    const explicitField =
      domNode.getAttribute("data-hubspot-field") ||
      domNode.getAttribute("data-hs-field") ||
      "";
    if (explicitField) {
      return resolveHubspotFieldAlias(explicitField);
    }
    if (domNode.type === "email") return "email";
    if (domNode.type === "tel") return "phone";
    if (domNode.getAttribute("inputmode") === "email") return "email";
    if (domNode.getAttribute("inputmode") === "tel") return "phone";
  }

  const metaLabel = stripLabelText(fieldMeta?.text || fieldMeta?.label || "");
  const labelText = getFieldLabelText(domNode, fieldMeta) || metaLabel;
  if (!labelText && !metaLabel) return null;

  if (labelLooksLikeEmail(labelText) || labelLooksLikeEmail(metaLabel)) return "email";
  if (labelLooksLikeNoOfAgents(labelText) || labelLooksLikeNoOfAgents(metaLabel)) {
    return "number_of_agents";
  }
  if (labelLooksLikeLastName(labelText) || labelLooksLikeLastName(metaLabel)) {
    return "lastname";
  }
  if (labelLooksLikeFirstName(labelText) || labelLooksLikeFirstName(metaLabel)) {
    return "firstname";
  }
  if (
    labelText.includes("company") ||
    labelText.includes("organisation") ||
    labelText.includes("organization") ||
    labelText.includes("org")
  ) {
    return "company";
  }
  if (labelText.includes("phone") || labelText.includes("mobile")) return "phone";
  if (
    labelText.includes("job") ||
    labelText.includes("title") ||
    labelText.includes("designation")
  ) {
    return "jobtitle";
  }
  if (labelText.includes("country")) return "country";
  if (
    labelText.includes("consent") ||
    labelText.includes("communique") ||
    labelText.includes("receive") ||
    labelText.includes("subscribe")
  ) {
    return "receive_tcl_communique__c";
  }

  return null;
}

function collectValuesFromContainer(container) {
  const result = {};

  function traverse(node) {
    if (node instanceof HTMLInputElement) {
      if (!node.id) return;
      if (node.type === "checkbox") result[node.id] = node.checked;
      else if (node.type === "radio") {
        if (node.checked) result[node.id] = node.value;
      } else {
        result[node.id] = node.value;
      }
    } else if (node instanceof HTMLTextAreaElement) {
      if (node.id) result[node.id] = node.value;
    } else if (node instanceof HTMLSelectElement) {
      if (node.id) result[node.id] = node.value;
    } else {
      Array.from(node.children || []).forEach((child) => traverse(child));
    }
  }

  traverse(container);
  return result;
}

export function collectPopupLeadData({
  popupContainer,
  popupFieldUuids,
  roiActionArr,
  findFieldDomNode,
}) {
  if (!popupContainer) return {};

  const lead = {};
  const fieldUuids = popupFieldUuids?.size
    ? Array.from(popupFieldUuids)
    : Object.keys(collectValuesFromContainer(popupContainer));

  fieldUuids.forEach((uuid) => {
    const meta = roiActionArr?.[uuid];
    if (!meta || meta.type === "Button") return;

    const domNode = findFieldDomNode ? findFieldDomNode(uuid) : document.getElementById(uuid);
    if (domNode && !popupContainer.contains(domNode)) return;

    if (meta.type === "Checkbox Option" || meta.type === "Checkbox2 Option") {
      const isSelected =
        meta.selected === true ||
        meta.selected === 1 ||
        (domNode instanceof HTMLInputElement && domNode.checked);
      if (!isSelected) return;

      const hsField =
        inferHubspotFieldName(uuid, meta, domNode) || "receive_tcl_communique__c";
      lead[hsField] = "true";
      return;
    }

    const rawValue = readRoiFieldValue(uuid, meta, findFieldDomNode);
    if (rawValue === null || rawValue === "" || rawValue === undefined || rawValue === false) {
      return;
    }

    const hsField = inferHubspotFieldName(uuid, meta, domNode);
    if (!hsField) return;

    lead[hsField] = String(rawValue).trim();
  });

  if (!lead.email) {
    const emailCandidates = popupContainer.querySelectorAll(
      'input[type="email"], input[inputmode="email" i], input[name*="email" i]'
    );
    for (const input of emailCandidates) {
      const val = String(input.value || "").trim();
      if (val && isValidEmail(val)) {
        lead.email = val;
        break;
      }
    }
  }

  return lead;
}

function readRoiFieldValue(uuid, meta, findFieldDomNode) {
  const domNode = findFieldDomNode ? findFieldDomNode(uuid) : document.getElementById(uuid);

  if (
    domNode instanceof HTMLInputElement ||
    domNode instanceof HTMLTextAreaElement ||
    domNode instanceof HTMLSelectElement
  ) {
    const domVal = domNode.value;
    if (domVal !== "" && domVal != null) return domVal;
  }

  const stateVal = meta?.value ?? meta?.default_value;
  if (stateVal !== "" && stateVal != null && stateVal !== undefined) {
    return stateVal;
  }

  return null;
}

export function appendCalculatorFieldsToLead(
  lead,
  { roiActionArr, landingContainer, popupContainer, findFieldDomNode } = {}
) {
  const nextLead = { ...(lead || {}) };
  if (nextLead.number_of_agents || nextLead.no_of_agents) return nextLead;

  const containers = [landingContainer, typeof document !== "undefined" ? document.body : null].filter(
    Boolean
  );

  for (const uuid of Object.keys(roiActionArr || {})) {
    const meta = roiActionArr[uuid];
    if (!meta || meta.type === "Button") continue;

    const domNode = findFieldDomNode ? findFieldDomNode(uuid) : document.getElementById(uuid);
    if (popupContainer && domNode && popupContainer.contains(domNode)) continue;

    const hsField = inferHubspotFieldName(uuid, meta, domNode);
    if (hsField !== "number_of_agents" && hsField !== "no_of_agents") continue;

    const raw = readRoiFieldValue(uuid, meta, findFieldDomNode);
    if (raw !== null && raw !== "" && raw !== undefined) {
      nextLead.number_of_agents = String(raw).trim();
      return nextLead;
    }
  }

  for (const container of containers) {
    const nodes = container.querySelectorAll("input[id], textarea[id], select[id]");
    for (const node of nodes) {
      if (popupContainer && popupContainer.contains(node)) continue;

      const meta = roiActionArr?.[node.id];
      const hsField = inferHubspotFieldName(node.id, meta, node);
      if (hsField !== "number_of_agents" && hsField !== "no_of_agents") continue;

      const raw = node.value ?? meta?.value ?? meta?.default_value;
      if (raw !== null && raw !== "" && raw !== undefined) {
        nextLead.number_of_agents = String(raw).trim();
        return nextLead;
      }
    }
  }

  return nextLead;
}

export function initHubspotForm({
  portalId = "47271964",
  formId = "a832c87d-5ad6-4fa4-acb0-8138423a9af5",
  region = "na1",
  target = "#roi-hubspot-form-host",
} = {}) {
  if (hubspotFormRef && isHubspotFormConnected()) {
    return Promise.resolve(hubspotFormRef);
  }

  if (hubspotFormRef && !isHubspotFormConnected()) {
    resetHubspotFormState();
  }

  if (hubspotInitPromise) return hubspotInitPromise;

  bindHubspotPostMessageListener();

  hubspotInitPromise = (async () => {
    const host = ensureHubspotFormHost() || document.querySelector(target);
    if (!host) {
      throw new Error(`[ROI HubSpot] Host element not found: ${target}`);
    }

    await loadHubspotScript();

    if (!window.hbspt?.forms?.create) {
      throw new Error("HubSpot forms library is not available");
    }

    window.hbspt.forms.create({
      portalId,
      formId,
      region,
      target,
      onFormReady: ($form) => {
        hubspotFormRef = $form;
        console.log("[ROI HubSpot] Hidden form ready");
        if (hubspotFormReadyResolve) {
          hubspotFormReadyResolve($form);
          hubspotFormReadyResolve = null;
        }
      },
      onFormSubmitted: () => {
        console.log("[ROI HubSpot] onFormSubmitted callback fired");
        trackMunchkinPageVisit();
      },
    });

    return waitForHubspotForm();
  })().catch((err) => {
    hubspotInitPromise = null;
    throw err;
  });

  return hubspotInitPromise;
}

function setHubSpotCheckboxField($form, name, checked = true) {
  const wantTrue = checked === true || checked === "true" || checked === 1;
  const formEl = getHubSpotFormElement($form);
  if (!formEl) return false;

  const inputs = Array.from(
    formEl.querySelectorAll(`input[name="${name}"], input[name="${name}[]"]`)
  );

  if (!inputs.length) return false;

  let changed = false;
  inputs.forEach((input) => {
    if (input.type !== "checkbox" && input.type !== "radio") return;
    const isTrueOption = String(input.value).toLowerCase() === "true";
    const shouldCheck = wantTrue ? isTrueOption : !isTrueOption;
    if (input.checked !== shouldCheck) {
      input.checked = shouldCheck;
      changed = true;
    }
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  return changed || inputs.length > 0;
}

export function normalizeLeadForHubspot(lead = {}) {
  const normalized = { ...(lead || {}) };
  let first = String(normalized.firstname || "").trim();
  let last = String(normalized.lastname || "").trim();

  // Only split when user entered a full name in firstname and left lastname empty
  if (!last && first.includes(" ")) {
    const parts = first.split(/\s+/).filter(Boolean);
    if (parts.length > 1) {
      normalized.firstname = parts[0];
      normalized.lastname = parts.slice(1).join(" ");
      first = normalized.firstname;
      last = normalized.lastname;
    }
  }

  if (first && last) {
    normalized.firstname = first;
    normalized.lastname = last;
  } else if (first && !last) {
    normalized.firstname = first;
    normalized.lastname = "-";
  } else if (!first && last) {
    normalized.firstname = last;
    normalized.lastname = last;
  } else {
    normalized.firstname = "-";
    normalized.lastname = "-";
  }

  if (!String(normalized.jobtitle || "").trim()) {
    normalized.jobtitle = "-";
  }

  if (
    normalized.receive_tcl_communique__c === undefined ||
    normalized.receive_tcl_communique__c === null ||
    normalized.receive_tcl_communique__c === ""
  ) {
    normalized.receive_tcl_communique__c = "true";
  }

  normalized.marketo_form_id = normalized.marketo_form_id || "7370";

  if (normalized.no_of_agents && !normalized.number_of_agents) {
    normalized.number_of_agents = normalized.no_of_agents;
  }
  delete normalized.no_of_agents;

  return normalized;
}

function getHubspotUtk() {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : "";
}

async function submitViaHubspotFormsApi(lead) {
  const portalId = process.env.REACT_APP_HUBSPOT_PORTAL_ID || "47271964";
  const formId =
    process.env.REACT_APP_HUBSPOT_FORM_ID || "a832c87d-5ad6-4fa4-acb0-8138423a9af5";
  const agentsField =
    process.env.REACT_APP_HUBSPOT_AGENTS_FIELD || "number_of_agents";

  const payloadFields = [
    ["email", lead.email],
    ["firstname", lead.firstname],
    ["lastname", lead.lastname],
    ["phone", lead.phone],
    ["jobtitle", lead.jobtitle],
    ["company", lead.company],
    [agentsField, lead[agentsField] || lead.number_of_agents || lead.no_of_agents],
    ["number_of_agents", lead.number_of_agents || lead.no_of_agents],
    ["receive_tcl_communique__c", lead.receive_tcl_communique__c],
    ["marketo_form_id", lead.marketo_form_id],
  ];

  const fields = payloadFields
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
    .map(([name, value]) => ({
      objectTypeId: "0-1",
      name,
      value: String(value).trim(),
    }));

  const context = {
    pageUri: typeof window !== "undefined" ? window.location.href : "",
    pageName: typeof document !== "undefined" ? document.title || "ROI Calculator" : "ROI Calculator",
  };
  const hutk = getHubspotUtk();
  if (hutk) {
    context.hutk = hutk;
  }

  const payload = {
    submittedAt: Date.now(),
    fields,
    context,
  };

  const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;
  console.log("[ROI HubSpot] POST", url, payload);

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }

  if (!res.ok) {
    console.warn("[ROI HubSpot] API submit failed", res.status, body);
    return false;
  }

  console.log("[ROI HubSpot] API submit success", body);
  return true;
}

function populateHiddenHubspotForm($form, lead) {
  setHubSpotField($form, 'input[name="email"], input[type="email"]', lead.email);
  setHubSpotField($form, 'input[name="firstname"]', lead.firstname);
  setHubSpotField($form, 'input[name="lastname"]', lead.lastname);
  setHubSpotField($form, 'input[name="jobtitle"]', lead.jobtitle);
  setHubSpotCompanyField($form, lead.company);
  setHubSpotField($form, 'input[name="phone"], input[name="mobilephone"]', lead.phone);
  setHubSpotField($form, 'input[name="marketo_form_id"]', lead.marketo_form_id);
  setHubSpotCheckboxField($form, "receive_tcl_communique__c", lead.receive_tcl_communique__c);

  const agentsFieldName =
    process.env.REACT_APP_HUBSPOT_AGENTS_FIELD || "number_of_agents";
  const agentsValue = lead[agentsFieldName] || lead.number_of_agents || lead.no_of_agents;
  if (agentsValue) {
    setHubSpotField(
      $form,
      `input[name="${agentsFieldName}"], textarea[name="${agentsFieldName}"], input[name="number_of_agents"], textarea[name="number_of_agents"], input[name="no_of_agents"], textarea[name="no_of_agents"]`,
      agentsValue
    );
  }
}

export async function submitLeadToHubspot(leadData = {}) {
  if (!isHubspotIntegrationEnabled()) {
    console.warn("[ROI HubSpot] Integration disabled");
    return false;
  }

  const lead = normalizeLeadForHubspot(leadData || {});
  const email = String(lead.email || "").trim();

  if (!isValidEmail(email)) {
    console.warn("[ROI HubSpot] Invalid or missing email in lead data:", lead.email);
    return false;
  }

  lead.email = email;
  console.log("[ROI HubSpot] Submitting normalized lead:", lead);

  if (!isHubspotFormConnected()) {
    resetHubspotFormState();
    await bootHubspotIntegration();
  }

  let $form = hubspotFormRef;

  if (!$form) {
    console.log("[ROI HubSpot] Waiting for hidden form...");
    try {
      $form = await waitForHubspotForm();
    } catch (waitErr) {
      console.warn(waitErr.message);
      await initHubspotForm({
        portalId: process.env.REACT_APP_HUBSPOT_PORTAL_ID || "47271964",
        formId:
          process.env.REACT_APP_HUBSPOT_FORM_ID || "a832c87d-5ad6-4fa4-acb0-8138423a9af5",
        region: process.env.REACT_APP_HUBSPOT_REGION || "na1",
        target: "#roi-hubspot-form-host",
      });
      $form = await waitForHubspotForm();
    }
  }

  console.log("[ROI HubSpot] Populating hidden form fields...");
  populateHiddenHubspotForm($form, lead);
  await delay(200);

  let submitted = false;

  try {
    submitted = await submitViaHubspotFormsApi(lead);
  } catch (apiErr) {
    console.warn("[ROI HubSpot] API submit error:", apiErr);
  }

  if (!submitted) {
    const hubspotEmail = readHubSpotFieldValue(
      $form,
      'input[name="email"], input[type="email"]'
    );

    if (!isValidEmail(hubspotEmail)) {
      setHubSpotField($form, 'input[name="email"], input[type="email"]', email);
      await delay(150);
    }

    const formEl = getHubSpotFormElement($form);
    const submitBtn = formEl?.querySelector(
      'input[type="submit"], button[type="submit"], .hs-button'
    );

    if (formEl && typeof formEl.requestSubmit === "function") {
      formEl.requestSubmit(submitBtn || undefined);
      console.log("[ROI HubSpot] Hidden form requestSubmit() fired");
      submitted = true;
    } else if (submitBtn) {
      submitBtn.click();
      console.log("[ROI HubSpot] Hidden form submit button clicked");
      submitted = true;
    } else {
      console.warn("[ROI HubSpot] Submit button not found on hidden form");
    }
  }

  if (submitted) {
    await delay(150);
    await trackMunchkinPageVisit();
  }

  return submitted;
}
